"use client";

import { cn } from "@gettactic/helpers";
import { getApiErrorMessage } from "@gettactic/helpers/src/errors/getApiErrorMessage";
import { FileUp, Loader2, Paperclip, X } from "lucide-react";
import React, { useRef, useState } from "react";
import toast from "react-hot-toast";
import { uploadToSignedSlot } from "../../upload/uploadToSignedSlot";
import { FormControlWrapper } from "../FormControlWrapper";
import { FileUploadInputProps } from "./FileUploadInput.types";

/**
 * Reads back the value stored for a file field. While filling the form the value is the
 * upload input (`{ path, filename }`); a value loaded from an API is usually the signed
 * response (`{ url, filename }`). Either way we only need the filename for display.
 */
const getStoredFilename = (value: unknown): string | null => {
  if (value && typeof value === "object" && "filename" in value) {
    const filename = (value as { filename?: unknown }).filename;
    return typeof filename === "string" && filename
      ? filename
      : "Attached file";
  }
  return null;
};

/**
 * Presentational file-upload form field: shows a dashed "upload" button or the attached
 * file's name, wrapped in the shared `FormControlWrapper` chrome. Controlled via
 * `value`/`onChange`; the upload endpoint is injected via `generateUpload`.
 */
export const FileUploadInput: React.FC<FileUploadInputProps> = ({
  value,
  onChange,
  generateUpload,
  label,
  isRequired = false,
  description,
  error,
  disabled = false
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);
  const filename = getStoredFilename(value);

  const handleSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = ""; // allow re-selecting the same file
    if (!file) {
      return;
    }
    setIsUploading(true);
    try {
      onChange(await uploadToSignedSlot(file, generateUpload));
    } catch (uploadError) {
      toast.error(getApiErrorMessage(uploadError, "Failed to upload file"));
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <FormControlWrapper
      variant="unstyled"
      label={label}
      isRequired={isRequired}
      helperText={description}
      error={error}
    >
      {filename ? (
        <div className="flex items-center justify-between gap-3 rounded-md border border-neutral-200 px-3 py-2.5">
          <span className="flex min-w-0 items-center gap-2 text-neutral-700 text-sm">
            <Paperclip className="h-4 w-4 shrink-0 text-neutral-400" />
            <span className="truncate">{filename}</span>
          </span>
          <button
            type="button"
            onClick={() => onChange("")}
            className="shrink-0 text-neutral-400 hover:text-neutral-700"
            aria-label="Remove file"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={disabled || isUploading}
          className={cn(
            "flex w-full items-center justify-center gap-2 rounded-md border border-neutral-200 border-dashed px-3 py-3 text-neutral-500 text-sm transition-colors",
            disabled || isUploading
              ? "cursor-not-allowed opacity-70"
              : "hover:border-neutral-300 hover:text-neutral-700"
          )}
        >
          {isUploading ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Uploading…
            </>
          ) : (
            <>
              <FileUp className="h-4 w-4" />
              Upload a file
            </>
          )}
        </button>
      )}

      <input
        ref={inputRef}
        type="file"
        className="hidden"
        onChange={handleSelect}
      />
    </FormControlWrapper>
  );
};
